import type { AxiosRequestConfig, AxiosResponse } from 'axios';

export interface MockedResponse {
  url: string;
  method: string;
  data: unknown;
  headers: Record<string, string>;
  status: number;
  statusText: string;
  timestamp: string;
  requestData?: unknown;
  params?: unknown;
}

export interface MockDataStore {
  [key: string]: MockedResponse;
}

class MockDataManager {
  private store: MockDataStore = {};
  private storageKey = 'api-mock-data';
  private isInitialized = false;
  private fileDataLoaded = false;

  constructor() {
    this.loadFromStorage();
  }

  private generateKey(config: AxiosRequestConfig): string {
    const url = config.url || '';
    const method = (config.method || 'GET').toUpperCase();
    const params = config.params ? JSON.stringify(config.params) : '';
    const data = config.data ? JSON.stringify(config.data) : '';
    
    // Create a hash-like key from the request details
    const keyString = `${method}:${url}:${params}:${data}`;
    return btoa(keyString).replace(/[+/=]/g, '').substring(0, 32);
  }

  private loadFromStorage(): void {
    try {
      // Check if localStorage is available (e.g., in test environments)
      if (typeof localStorage !== 'undefined' && localStorage.getItem) {
        const stored = localStorage.getItem(this.storageKey);
        if (stored) {
          this.store = JSON.parse(stored);
        }
      }
    } catch (error) {
      console.warn('Failed to load mock data from storage:', error);
      this.store = {};
    }
    this.isInitialized = true;
  }

  private saveToStorage(): void {
    try {
      // Check if localStorage is available (e.g., in test environments)
      if (typeof localStorage !== 'undefined' && localStorage.setItem) {
        localStorage.setItem(this.storageKey, JSON.stringify(this.store));
      }
    } catch (error) {
      console.warn('Failed to save mock data to storage:', error);
    }
  }

  public async loadFromFile(filePath: string = '/mockData.json'): Promise<void> {
    try {
      const response = await fetch(filePath);
      if (response.ok) {
        const fileData = await response.json();
        this.store = { ...this.store, ...fileData };
        this.fileDataLoaded = true;
        console.log(`[Mock Data] Loaded ${Object.keys(fileData).length} mock responses from ${filePath}`);
      } else {
        console.log(`[Mock Data] No mock data file found at ${filePath}`);
      }
    } catch (error) {
      console.log(`[Mock Data] Could not load mock data from ${filePath}:`, error);
    }
  }

  public async initializeForReplay(): Promise<void> {
    if (!this.fileDataLoaded) {
      await this.loadFromFile();
    }
  }

  public saveResponse(config: AxiosRequestConfig, response: AxiosResponse): void {
    const key = this.generateKey(config);
    const mockedResponse: MockedResponse = {
      url: config.url || '',
      method: (config.method || 'GET').toUpperCase(),
      data: response.data,
      headers: response.headers as Record<string, string>,
      status: response.status,
      statusText: response.statusText,
      timestamp: new Date().toISOString(),
      requestData: config.data,
      params: config.params
    };

    this.store[key] = mockedResponse;
    this.saveToStorage();
    
    console.log(`[Mock Data] Saved response for ${mockedResponse.method} ${mockedResponse.url}`);
  }

  public getResponse(config: AxiosRequestConfig): MockedResponse | null {
    const key = this.generateKey(config);
    return this.store[key] || null;
  }

  public hasResponse(config: AxiosRequestConfig): boolean {
    const key = this.generateKey(config);
    return key in this.store;
  }

  public clearAllData(): void {
    this.store = {};
    this.saveToStorage();
    console.log('[Mock Data] Cleared all mock data');
  }

  public exportData(): string {
    return JSON.stringify(this.store, null, 2);
  }

  public importData(jsonData: string): void {
    try {
      const imported = JSON.parse(jsonData);
      this.store = { ...this.store, ...imported };
      this.saveToStorage();
      console.log('[Mock Data] Imported mock data');
    } catch (error) {
      console.error('Failed to import mock data:', error);
    }
  }

  public getStoredResponses(): MockedResponse[] {
    return Object.values(this.store);
  }

  public getStats(): { total: number; byMethod: Record<string, number> } {
    const responses = this.getStoredResponses();
    const byMethod = responses.reduce((acc, response) => {
      acc[response.method] = (acc[response.method] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total: responses.length,
      byMethod
    };
  }
}

export const mockDataManager = new MockDataManager();

// Development utilities - only available in development mode
export const mockUtils = {
  exportToFile: () => {
    const data = mockDataManager.exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mockData.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  importFromFile: (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      mockDataManager.importData(content);
    };
    reader.readAsText(file);
  },

  loadFromRepo: () => mockDataManager.loadFromFile(),

  getStats: () => mockDataManager.getStats(),
  
  clearAll: () => mockDataManager.clearAllData()
};

// Make utilities available on window for easy access in dev tools
if (import.meta.env.DEV) {
  (window as Window & { mockUtils?: typeof mockUtils }).mockUtils = mockUtils;
} 